import { Box, Button, Stack, Tooltip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { type Draft } from '@lib/customization/poiDraft';
import { LocalizationManager, useLocalization } from '@lib/localization';
import {
  formInputTypes, newEntityId, ruleOperators, type PoiField, type PoiFieldGroup, type RuleOperator,
} from '@store/api/poiTypeEndpoints';
import { useRef, useState } from 'react';
import { AlignLeftIcon, CheckCircleIcon, CopyIcon, ExclamationTriangleIcon } from 'react-line-awesome';
import { WARN_COLOR } from './designer.styles';

interface Props {
  /** The draft when the editor opened. The editor owns its text from then on. */
  value: Draft;
  /** Called with every edit that parses and has the right shape. */
  onChange: (draft: Draft) => void;
  /** The server's answer to the last save, when it refused it. */
  serverError?: string | null;
  /** Problems in a draft that parses: things the save would refuse or silently change. */
  issues?: string[];
  disabled?: boolean;
}

type Problem = { message: string; line?: number; column?: number };

const LINE_HEIGHT = 20;

const isObject = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const isLocalized = (value: unknown) => isObject(value) && Object.values(value).every((text) => typeof text === 'string');

/** What is shown: exactly the stored shape, two-space indented. */
const draftJson = (draft: Draft): string => JSON.stringify(draft, null, 2);

/**
 * Where a parse failed, from the engine's message. V8 says either "at position N" or, more
 * recently, "(line L column C)"; anything else just has no position.
 */
const parseProblem = (error: unknown, text: string): Problem => {
  const raw = error instanceof Error ? error.message : String(error);
  // The engine appends its own position; ours is shown in front, so one is enough.
  const message = raw.replace(/\s*in JSON at position \d+.*$/, '').replace(/\s*\(line \d+ column \d+\)$/, '');
  const lineColumn = /line (\d+) column (\d+)/.exec(raw);

  if (lineColumn) return { message, line: Number(lineColumn[1]), column: Number(lineColumn[2]) };

  const position = /position (\d+)/.exec(raw);

  if (!position) return { message };

  const before = text.slice(0, Number(position[1])).split('\n');

  return { message, line: before.length, column: before[before.length - 1].length + 1 };
};

/**
 * The shape the designer needs to draw the type, checked before it replaces the draft. Not
 * the server's validation: that runs on save and knows every per-type rule; this only keeps
 * the canvas from being handed something it cannot draw.
 */
const shapeProblem = (data: unknown): string | null => {
  const { t } = LocalizationManager;

  if (!isObject(data)) return t('Customization_Designer_JsonNotObject');
  if (data.name !== undefined && !isLocalized(data.name)) return `name: ${t('Customization_Designer_JsonLocalized')}`;
  if (data.description !== undefined && data.description !== null && typeof data.description !== 'string') {
    return `description: ${t('Customization_Designer_JsonString')}`;
  }
  if (!Array.isArray(data.fields)) return `fields: ${t('Customization_Designer_JsonArray')}`;
  if (!Array.isArray(data.groups)) return `groups: ${t('Customization_Designer_JsonArray')}`;

  // Rules are read by the canvas as they are typed, so a broken one would break the page:
  // its shape is checked here, and what the conditions say is checked on save.
  const rulesProblem = (rules: unknown, path: string): string | null => {
    if (rules === undefined || rules === null) return null;
    if (!Array.isArray(rules)) return `${path}.rules: ${t('Customization_Designer_JsonArray')}`;

    for (const [index, rule] of rules.entries()) {
      const at = `${path}.rules[${index}]`;

      if (!isObject(rule) || !isObject(rule.when) || !isObject(rule.then)) return `${at}: ${t('Customization_Designer_JsonRule')}`;
      if (rule.when.match !== 'all' && rule.when.match !== 'any') return `${at}.when.match: ${t('Customization_Designer_JsonOneOf', ['all, any'])}`;
      if (!Array.isArray(rule.when.conditions)) return `${at}.when.conditions: ${t('Customization_Designer_JsonArray')}`;

      for (const [conditionIndex, condition] of rule.when.conditions.entries()) {
        const conditionAt = `${at}.when.conditions[${conditionIndex}]`;

        if (!isObject(condition) || typeof condition.fieldId !== 'string') return `${conditionAt}.fieldId: ${t('Customization_Designer_JsonString')}`;
        if (!ruleOperators.includes(condition.operator as RuleOperator)) {
          return `${conditionAt}.operator: ${t('Customization_Designer_JsonOneOf', [ruleOperators.join(', ')])}`;
        }
      }
    }

    return null;
  };

  const fieldProblem = (field: unknown, path: string): string | null => {
    if (!isObject(field)) return `${path}: ${t('Customization_Designer_JsonObject')}`;
    if (!formInputTypes.includes(field.type as PoiField['type'])) {
      return `${path}.type: ${t('Customization_Designer_JsonOneOf', [formInputTypes.join(', ')])}`;
    }
    if (!isLocalized(field.label)) return `${path}.label: ${t('Customization_Designer_JsonLocalized')}`;
    if (field.options !== undefined && !Array.isArray(field.options)) return `${path}.options: ${t('Customization_Designer_JsonArray')}`;

    for (const [index, option] of ((field.options as unknown[]) ?? []).entries()) {
      if (!isObject(option) || !isLocalized(option.label)) return `${path}.options[${index}].label: ${t('Customization_Designer_JsonLocalized')}`;
    }

    return rulesProblem(field.rules, path);
  };

  for (const [index, field] of data.fields.entries()) {
    const problem = fieldProblem(field, `fields[${index}]`);

    if (problem) return problem;
  }

  for (const [index, group] of data.groups.entries()) {
    if (!isObject(group)) return `groups[${index}]: ${t('Customization_Designer_JsonObject')}`;
    if (!isLocalized(group.name)) return `groups[${index}].name: ${t('Customization_Designer_JsonLocalized')}`;
    if (!Array.isArray(group.fields)) return `groups[${index}].fields: ${t('Customization_Designer_JsonArray')}`;

    const problem = rulesProblem(group.rules, `groups[${index}]`);

    if (problem) return problem;

    for (const [fieldIndex, field] of group.fields.entries()) {
      const problem = fieldProblem(field, `groups[${index}].fields[${fieldIndex}]`);

      if (problem) return problem;
    }
  }

  return null;
};

/**
 * Entries pasted without an id get one, so the canvas can select them and rules can point
 * at them; the server would mint one anyway, but only on save.
 */
const withIds = (data: Draft): Draft => {
  const field = (entry: PoiField): PoiField => ({
    ...entry,
    id: entry.id ?? newEntityId(),
    options: entry.options?.map((option) => ({ ...option, id: option.id ?? newEntityId() })),
  });

  return {
    ...data,
    fields: data.fields.map(field),
    groups: data.groups.map((group: PoiFieldGroup) => ({ ...group, id: group.id ?? newEntityId(), fields: group.fields.map(field) })),
  };
};

/**
 * The type as JSON: the stored shape, edited as text.
 *
 * Every edit that parses and has the right shape replaces the draft at once, so switching
 * back to Design shows it; one that does not keeps the last good draft and says where it
 * broke. Save and Discard are the designer's, the same as for every other edit.
 */
const JsonEditor: React.FC<Props> = ({ value, onChange, serverError, issues = [], disabled }) => {
  const { t } = useLocalization();

  const [text, setText] = useState(() => draftJson(value));
  const [problem, setProblem] = useState<Problem | null>(null);
  const [copied, setCopied] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);
  const gutter = useRef<HTMLDivElement>(null);

  const apply = (next: string) => {
    setText(next);

    let data: unknown;

    try {
      data = JSON.parse(next);
    } catch (error) {
      setProblem(parseProblem(error, next));
      return;
    }

    const shape = shapeProblem(data);

    if (shape) {
      setProblem({ message: shape });
      return;
    }

    setProblem(null);
    // Only what the type stores; anything else pasted in stays out of the draft.
    const { name, description, fields, groups } = data as Draft;

    onChange(withIds({ name, description, fields, groups }));
  };

  const lines = text.split('\n').length;

  // Tab indents rather than leaving the box; two spaces, the indent the text is written with.
  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Tab') return;

    event.preventDefault();

    const target = event.currentTarget;
    const { selectionStart, selectionEnd } = target;
    const next = `${text.slice(0, selectionStart)}  ${text.slice(selectionEnd)}`;

    apply(next);
    requestAnimationFrame(() => target.setSelectionRange(selectionStart + 2, selectionStart + 2));
  };

  const format = () => {
    try {
      apply(JSON.stringify(JSON.parse(text), null, 2));
    } catch {
      // Nothing to format until it parses; the problem line already says why.
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard needs a secure context or permission; selecting the text still works.
      area.current?.select();
    }
  };

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', my: 2 }}>
      {/* Status first, where the eye is: what is wrong and where, or that all is well. */}
      <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0, fontSize: 13 }}>
          {problem && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: WARN_COLOR }}>
              <ExclamationTriangleIcon />
              <span>
                {problem.line ? `${t('Customization_Designer_JsonAt', [problem.line, problem.column ?? 1])} ` : ''}
                {problem.message}
                {` ${t('Customization_Designer_JsonKeptLastGood')}`}
              </span>
            </Stack>
          )}
          {!problem && issues.length > 0 && (
            <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ color: WARN_COLOR, whiteSpace: 'pre-wrap' }}>
              <ExclamationTriangleIcon />
              <span>{`${t('Customization_Designer_FixBeforeSaving')}\n${issues.join('\n')}`}</span>
            </Stack>
          )}
          {!problem && !issues.length && serverError && (
            <Stack direction="row" spacing={0.75} alignItems="flex-start" sx={{ color: 'error.light', whiteSpace: 'pre-wrap' }}>
              <ExclamationTriangleIcon />
              <span>{`${t('Customization_Designer_JsonServerRefused')}\n${serverError}`}</span>
            </Stack>
          )}
          {!problem && !issues.length && !serverError && (
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ color: 'text.secondary' }}>
              <CheckCircleIcon />
              <span>{t('Customization_Designer_JsonHint')}</span>
            </Stack>
          )}
        </Box>
        <Button size="small" startIcon={<AlignLeftIcon />} disabled={disabled || Boolean(problem?.line)} onClick={format} sx={{ textTransform: 'none' }}>
          {t('Customization_Designer_JsonFormat')}
        </Button>
        <Tooltip title={copied ? t('Common_Copied') : ''} open={copied}>
          <Button size="small" startIcon={<CopyIcon />} onClick={() => void copy()} sx={{ textTransform: 'none' }}>
            {t('Customization_Designer_JsonCopy')}
          </Button>
        </Tooltip>
      </Stack>

      <Stack
        direction="row"
        sx={{
          height: 'calc(100vh - 380px)',
          minHeight: 360,
          borderRadius: 1.5,
          border: '1px solid',
          borderColor: problem ? alpha(WARN_COLOR, 0.6) : 'divider',
          backgroundColor: (theme) => alpha(theme.palette.common.black, 0.25),
          overflow: 'hidden',
          // The app's stylesheet forces its UI font with !important; code has to be monospace.
          '&, & *': { fontFamily: 'Consolas, "Cascadia Mono", Menlo, monospace !important' },
          fontSize: 13,
        }}
      >
        <Box
          ref={gutter}
          aria-hidden
          sx={{
            py: 1.25, px: 1, overflow: 'hidden', textAlign: 'right', userSelect: 'none', color: 'text.disabled',
            lineHeight: `${LINE_HEIGHT}px`, borderRight: '1px solid', borderColor: 'divider', minWidth: 44,
          }}
        >
          {Array.from({ length: lines }, (_, index) => (
            <Box key={index} sx={index + 1 === problem?.line ? { color: WARN_COLOR, fontWeight: 700 } : undefined}>
              {index + 1}
            </Box>
          ))}
        </Box>
        <Box
          component="textarea"
          ref={area}
          value={text}
          spellCheck={false}
          disabled={disabled}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => apply(event.target.value)}
          onKeyDown={onKeyDown}
          onScroll={(event: React.UIEvent<HTMLTextAreaElement>) => {
            if (gutter.current) gutter.current.scrollTop = event.currentTarget.scrollTop;
          }}
          sx={{
            flex: 1, py: 1.25, px: 1.5, border: 0, outline: 'none', resize: 'none', whiteSpace: 'pre', overflow: 'auto',
            background: 'transparent', color: 'text.primary', font: 'inherit', lineHeight: `${LINE_HEIGHT}px`, tabSize: 2,
          }}
        />
      </Stack>

    </Box>
  );
};

export default JsonEditor;
